import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicHero from './components/ComicHero';
import ComicInfo from './components/ComicInfo';
import ComicReviews from './components/ComicReviews';
import RelatedComics from './components/RelatedComics';
import { getComicBySlug } from '@/mocks/comics';

const baseUrl = 'http://kiyvx.cn/';

export default function ComicPage() {
  const { slug } = useParams<{ slug: string }>();
  const comic = slug ? getComicBySlug(slug) : undefined;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col items-center justify-center">
        <Navbar />
        <div className="flex-1 flex flex-col items-center justify-center gap-4">
          <h1 className="font-heading text-2xl font-bold text-foreground-900">漫画未找到</h1>
          <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm cursor-pointer">
            返回首页
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const pageTitle = `${comic.title}漫画 - 高清${comic.title}漫画全集免费阅读下拉式`;
  const pageDesc = `178漫画网${comic.title}漫画：${comic.description.slice(0, 120)}。超清画质下拉式阅读，${comic.totalChapters}话连载${comic.status}，${comic.author}作品。`;

  const comicJsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: baseUrl },
        {
          '@type': 'ListItem',
          position: 2,
          name: comic.category,
          item: `${baseUrl}/category/${comic.categorySlug}`,
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: comic.title,
          item: `${baseUrl}/comic/${comic.slug}`,
        },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: pageTitle,
      description: comic.description,
      author: { '@type': 'Person', name: comic.author },
      datePublished: comic.chapters[0]?.updatedAt || '2026-01-01',
      dateModified: [...comic.chapters].sort((a, b) => b.number - a.number)[0]?.updatedAt || '2026-07-01',
      image: comic.coverImage,
      url: `${baseUrl}/comic/${comic.slug}`,
      publisher: {
        '@type': 'Organization',
        name: '178漫画网',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: `${comic.title}在哪里可以免费阅读？`,
          acceptedAnswer: {
            '@type': 'Answer',
            text: `${comic.title}高清漫画全集可以在178漫画网免费阅读下拉式观看，超清画质流畅体验，无需下载。`,
          },
        },
        {
          '@type': 'Question',
          name: `${comic.title}多久更新一次？`,
          acceptedAnswer: {
            '@type': 'Answer',
            text: `${comic.title}${comic.updateFrequency}，目前${comic.status}，已有${comic.totalChapters}话。`,
          },
        },
        {
          '@type': 'Question',
          name: `${comic.title}是什么类型的漫画？`,
          acceptedAnswer: {
            '@type': 'Answer',
            text: `${comic.title}是${comic.category}题材漫画，由${comic.author}创作，评分${comic.rating}分，深受读者喜爱。`,
          },
        },
      ],
    },
  ];

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        keywords={`${comic.title},高清,漫画全集,免费阅读,下拉式,178漫画网,${comic.category}漫画,${comic.author}`}
        canonicalUrl={`${baseUrl}/comic/${comic.slug}`}
        ogType="article"
        ogImage={comic.coverImage}
        jsonLd={comicJsonLd}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main>
          <ComicHero />
          <ComicInfo />
          <ComicReviews />
          <RelatedComics />
        </main>
        <Footer />
      </div>
    </>
  );
}